/**
 * Modal layer.
 *
 * One component for every blocking dialog: the deck picker, the prompts the
 * engine raises through `pendingChoices`, concede confirmation and the game
 * over screen. Escape closes it unless `dismissible` is false, which is how a
 * required engine prompt stays on screen until it is answered.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { UiIcon } from '@/ui/icons/UiIcon';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  title: string;
  /** Short line under the title. Explains the choice, never scolds. */
  subtitle?: string;
  onClose?: () => void;
  /** False for engine prompts that must be answered. */
  dismissible?: boolean;
  size?: 'small' | 'medium' | 'large';
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  dismissible = true,
  size = 'medium',
  children,
  footer,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !dismissible || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissible, onClose]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (dismissible && onClose && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`${styles.panel} ${styles[size]}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>{title}</h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          {dismissible && onClose ? (
            <button className={styles.close} onClick={onClose} aria-label="Close">
              <UiIcon name="close" size={16} />
            </button>
          ) : null}
        </header>
        <div className={styles.content}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
